import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { getCached, setCached, buildCacheKey } from './utils/productCache';
import api from './utils/api';
import { useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CartSidebar from './components/CartSidebar';
import LoadingScreen from './components/LoadingScreen';
import ScrollToTop from './components/ScrollToTop';

import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductPage from './pages/ProductPage';

// Pages - Code Splitting (only for secondary routes now)
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const SubscriptionsPage = lazy(() => import('./pages/SubscriptionsPage'));
const SubscriptionPreferencesPage = lazy(() => import('./pages/SubscriptionPreferencesPage'));
const CustomBouquetPage = lazy(() => import('./pages/CustomBouquetPage'));

// Admin-only Protected Route Component
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingScreen message="Verifying admin access..." />;
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return <LoginPage />;
  }

  return children;
};

function App() {
  // Prefetch shop products on app load so it's instant when users visit
  React.useEffect(() => {
    const prefetchShop = async () => {
      try {
        const params = { page: 1, limit: 12 };
        const cacheKey = buildCacheKey(params);
        if (getCached(cacheKey)) return; // already cached

        const response = await api.get('/products', { params });
        const filteredProducts = response.data.products.filter(
          product => product.category?.toLowerCase() !== 'flowers'
        );
        
        setCached(cacheKey, { 
          products: filteredProducts, 
          pagination: response.data.pagination 
        });
      } catch (e) {
        // fail silently, user will just see loading spinner when they visit shop
        console.warn('Failed to prefetch shop products');
      }
    };
    
    // Slight delay to not block the main HomePage render
    setTimeout(prefetchShop, 1500);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <ScrollToTop />
      <Navbar />
      <main className="flex-grow">
        <Suspense fallback={<LoadingScreen message="Loading..." />}>
          <Routes>
            {/* Public Routes - Guest Shopping */}
            <Route path="/" element={<HomePage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/subscriptions/preferences" element={<SubscriptionPreferencesPage />} />
            <Route path="/custom-bouquet" element={<CustomBouquetPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order-confirmation/:orderId" element={<OrderConfirmationPage />} />

            {/* Admin Only Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/admin"
              element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              }
            />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CartSidebar />
    </div>
  );
}

export default App;
